import React from "react";
import { Link } from "react-router-dom";
import { Film, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";

const STAGE_KEYS = [
  "idea", "development", "script", "preproduction",
  "shooting", "postproduction", "distribution", "finished",
];
const FORMAT_KEYS = [
  "largometraje", "cortometraje", "serie", "miniserie",
  "documental", "programa", "digital", "branded",
];

export default function ProjectCard({ project, theme = "dark" }) {
  const { t } = useTranslation();
  const isDark = theme === "dark";
  const financing = Math.min(100, Math.round(project.financing_percent || 0));
  const cover = project.cover_url || "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=1200";

  const formatLabel = FORMAT_KEYS.includes(project.format)
    ? t(`project_card.format_${project.format}`)
    : project.format;
  const stageLabel = STAGE_KEYS.includes(project.stage)
    ? t(`project_card.stage_${project.stage}`)
    : project.stage;

  return (
    <Link
      to={`/proyecto/${project.id}`}
      data-testid={`card-project-${project.id}`}
      className={
        isDark
          ? "group block rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03] hover:border-gold/50 transition-colors"
          : "group block rounded-xl overflow-hidden border border-slate-200 bg-white hover:shadow-md transition-shadow"
      }
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={cover}
          alt={project.title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className={isDark ? "absolute inset-0 bg-gradient-to-t from-[#0B1320]/95 via-[#0B1320]/30 to-transparent" : "absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"} />
        <div className="absolute top-3 left-3 flex flex-wrap gap-2">
          {project.format && (
            <span className={isDark ? "eyebrow text-xs px-2.5 py-1 rounded-full bg-white/10 backdrop-blur text-white" : "eyebrow text-xs px-2.5 py-1 rounded-full bg-white/90 text-petroleum"}>
              {formatLabel}
            </span>
          )}
          {project.stage && (
            <span className={isDark ? "eyebrow text-xs px-2.5 py-1 rounded-full bg-gold text-petroleum" : "eyebrow text-xs px-2.5 py-1 rounded-full bg-gold text-petroleum"}>
              {stageLabel}
            </span>
          )}
        </div>
      </div>

      <div className={isDark ? "p-5 text-white" : "p-5"}>
        <h3 className={isDark ? "font-display text-2xl tracking-tight text-white group-hover:text-gold transition-colors" : "font-display text-2xl tracking-tight text-petroleum group-hover:text-gold transition-colors"}>
          {project.title}
        </h3>
        <p className={isDark ? "mt-2 text-sm text-white/70 line-clamp-2" : "mt-2 text-sm text-slate-500 line-clamp-2"}>
          {project.logline || project.synopsis_short}
        </p>

        <div className={isDark ? "mt-4 flex items-center gap-4 text-xs text-white/60" : "mt-4 flex items-center gap-4 text-xs text-slate-500"}>
          {project.origin_country && (
            <span className="inline-flex items-center gap-1">
              <MapPin size={12} /> {project.origin_country}
            </span>
          )}
          {project.genre && (
            <span className="inline-flex items-center gap-1">
              <Film size={12} /> {project.genre}
            </span>
          )}
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between text-xs">
            <span className={isDark ? "text-white/60" : "text-slate-500"}>{t("project_card.financed")}</span>
            <span className={isDark ? "text-gold font-semibold" : "text-petroleum font-semibold"}>{financing}%</span>
          </div>
          <div className={isDark ? "mt-2 h-1.5 rounded-full bg-white/10" : "mt-2 h-1.5 rounded-full bg-slate-100"}>
            <div className="h-1.5 rounded-full bg-gold" style={{ width: `${financing}%` }} />
          </div>
        </div>

        <div className="mt-4 flex gap-2">
          {project.seeks_placement && (
            <span className={isDark ? "text-[10px] eyebrow px-2 py-1 rounded-full border border-white/10 text-white/70" : "text-[10px] eyebrow px-2 py-1 rounded-full border border-slate-200 text-slate-500"}>{t("project_card.chip_placement")}</span>
          )}
          {project.seeks_sponsorship && (
            <span className={isDark ? "text-[10px] eyebrow px-2 py-1 rounded-full border border-white/10 text-white/70" : "text-[10px] eyebrow px-2 py-1 rounded-full border border-slate-200 text-slate-500"}>{t("project_card.chip_sponsorship")}</span>
          )}
          {project.seeks_cultural_funds && (
            <span className={isDark ? "text-[10px] eyebrow px-2 py-1 rounded-full border border-white/10 text-white/70" : "text-[10px] eyebrow px-2 py-1 rounded-full border border-slate-200 text-slate-500"}>{t("project_card.chip_funds")}</span>
          )}
        </div>
      </div>
    </Link>
  );
}
